<template>
  <div class="ebook-form-section">
    <div class="form-container">
      <div class="form-content">
        <div class="form-header">
          <div class="form-badge">
            <div class="badge-line"></div>
            <span class="badge-text">OBTÉN LA GUÍA</span>
          </div>
          <h2 class="form-title">
            Completa el formulario y recibe la guía para digitalizar tu mantenimiento
          </h2>
          <p class="form-description">
            Casos reales, buenas prácticas y pasos claros para activar OT, alertas y trazabilidad.
          </p>
        </div>

        <form class="ebook-form" @submit.prevent="handleSubmit">
          <div class="form-row">
            <div class="form-field">
              <label for="nombre" class="field-label">Nombre</label>
              <input
                id="nombre"
                v-model="formData.nombre"
                type="text"
                class="field-input"
                placeholder="Pedro"
                required
              />
            </div>
            <div class="form-field">
              <label for="apellido" class="field-label">Apellido</label>
              <input
                id="apellido"
                v-model="formData.apellido"
                type="text"
                class="field-input"
                placeholder="Pérez"
                required
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="email" class="field-label">Correo electrónico empresarial</label>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                class="field-input"
                placeholder="pperez@tuempresa.com"
                required
              />
            </div>
            <div class="form-field">
              <label for="celular" class="field-label">Celular</label>
              <input
                id="celular"
                v-model="formData.celular"
                type="tel"
                class="field-input"
                placeholder="312 345 6789"
                required
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label for="empresa" class="field-label">Empresa</label>
              <input
                id="empresa"
                v-model="formData.empresa"
                type="text"
                class="field-input"
                placeholder="Empresa S.A.S."
                required
              />
            </div>
            <div class="form-field">
              <label for="cargo" class="field-label">Cargo</label>
              <input
                id="cargo"
                v-model="formData.cargo"
                type="text"
                class="field-input"
                placeholder="Director de Operaciones"
                required
              />
            </div>
          </div>

          <button type="submit" class="download-button" :disabled="isLoading">
            <span v-if="!isLoading">Descargar ebook</span>
            <span v-else>Enviando...</span>
            <svg v-if="!isLoading" class="button-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div v-else class="loading-spinner"></div>
          </button>

          <!-- Mensaje de éxito -->
          <div v-if="isSubmitted" class="success-message">
            <svg class="success-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p>¡Formulario enviado correctamente! Revisa tu email para recibir el ebook.</p>
          </div>

          <!-- Mensaje de error -->
          <div v-if="errorMessage" class="error-message">
            <svg class="error-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p>{{ errorMessage }}</p>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'

// Datos del formulario
const formData = reactive({
  nombre: '',
  apellido: '',
  email: '',
  celular: '',
  empresa: '',
  cargo: '',
})

// Estado del formulario
const isLoading = ref(false)
const isSubmitted = ref(false)
const errorMessage = ref('')

// Función para manejar el envío del formulario
const handleSubmit = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // Detectar si estamos en modo local o producción
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

    let response
    if (isLocal) {
      // Modo local: usar función simulada
      const { sendEbookRequestLocal } = await import('@/lib/brevo-local')
      const result = await sendEbookRequestLocal(formData)
      response = { ok: true, json: () => Promise.resolve(result) }
    } else {
      // Modo producción: usar PHP endpoint en Hostinger
      response = await fetch('/api/sendMail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })
    }

    const result = await response.json()

    if (response.ok) {
      isSubmitted.value = true
      // Limpiar formulario
      Object.keys(formData).forEach(key => {
        formData[key as keyof typeof formData] = ''
      })
    } else {
      throw new Error(result.error || 'Error al enviar el formulario')
    }
  } catch (error) {
    console.error('Error:', error)
    errorMessage.value = error instanceof Error ? error.message : 'Error al enviar el formulario'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.ebook-form-section {
  padding: 80px 20px;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.form-container {
  max-width: 800px;
  width: 100%;
  display: flex;
  gap: 60px;
  align-items: flex-start;
}

.form-content {
  flex: 1;
}

.form-header {
  margin-bottom: 40px;
}

.form-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.badge-line {
  width: 40px;
  height: 2px;
  background: #129bef;
}

.badge-text {
  color: #129bef;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-title {
  font-size: clamp(1.8rem, 3vw, 2.2rem);
    font-weight: 700;
    color: #333;
    line-height: 1.3;
    letter-spacing: -0.5px;
    margin: 0 0 16px 0;
    letter-spacing: -0.02em;
}

.form-description {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.ebook-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.field-input {
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  color: #333;
  background: white;
  transition: all 0.3s ease;
  outline: none;
}

.field-input:focus {
  border-color: #129bef;
  box-shadow: 0 0 0 3px rgba(18, 155, 239, 0.1);
}

.field-input::placeholder {
  color: #999;
}

.download-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #129bef;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  width: 100%;
}

.download-button:hover {
  background: #0ea5e9;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 155, 239, 0.3);
}

.button-arrow {
  transition: transform 0.3s ease;
}

.download-button:hover .button-arrow {
  transform: translate(2px, -2px);
}

.download-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.download-button:disabled:hover {
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #d1fae5;
  color: #065f46;
  padding: 16px;
  border-radius: 8px;
  margin-top: 20px;
  border: 1px solid #a7f3d0;
}

.success-icon {
  color: #10b981;
  flex-shrink: 0;
}

.success-message p {
  margin: 0;
  font-weight: 500;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fee2e2;
  color: #991b1b;
  padding: 16px;
  border-radius: 8px;
  margin-top: 20px;
  border: 1px solid #fecaca;
}

.error-icon {
  color: #ef4444;
  flex-shrink: 0;
}

.error-message p {
  margin: 0;
  font-weight: 500;
}

@media (max-width: 768px) {
  .form-container {
    flex-direction: column;
    gap: 40px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .ebook-form-section {
    padding: 60px 15px;
  }

  .form-title {
    font-size: 1.6rem;
  }

  .form-description {
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .form-title {
    font-size: 1.4rem;
  }

  .field-input {
    padding: 10px 14px;
    font-size: 15px;
  }

  .download-button {
    padding: 14px 28px;
    font-size: 15px;
  }
}
</style>
